<script setup>
import { ref, onMounted, onUnmounted, inject, computed } from "vue";
import * as Sentry from "@sentry/vue";
import { getSettings } from "../utils/settings";
import { getLocation } from "../utils/getLocation";
import { DeviceInfo } from "../utils/deviceInfo.js";
import { hasSharedArrayBuffer } from "../utils/browser.js";
import { isLandscapeOrientation, onBrowserOrientationChange } from "../utils/browser.js";
import { loadThirdPartyContentRules, registerRules } from "../utils/thirdPartyContentManager.js";
import { redirectToPhotoAuth, isRedirectingToPhotoAuth } from "../utils/photoAuthRedirect.js";
import { generateUUID } from "../utils/uuid.js";

import stLogo from "../assets/logo.svg";

const props = defineProps({
  deviceInfo: DeviceInfo,
  onInitialized: Function,
  isCameraOpening: Function,
  toggleCameraAccessModal: Function,
  hasWasmCrashed: Boolean
});

const emit = defineEmits(["wechatInitializeClick"]);

const isSDKLoading = ref(true);
const isPrefixLoading = ref(true);
const isSettingsLoading = ref(true);
const hasCameraPermission = ref(null);
const hasLocationPermission = ref(null);
const isLocationPermissionDenied = ref(false);
const percentage = ref(10);
const installId = ref();
const coordinates = ref({});
const prefix = ref();
const settings = ref();
const isInitializing = ref(false);
const isWechat = props.deviceInfo.isWechat();
const useWorkers = hasSharedArrayBuffer();
const cameraPermissionRequested = ref(false);

function initInstallId() {
  const storedId = window.localStorage.getItem("installId");
  installId.value = storedId || generateUUID();
  if (!storedId) {
    window.localStorage.setItem("installId", installId.value);
  }
}

const brandingTheme = inject("brandingTheme");

const mainLogo = computed(() => (brandingTheme.value && brandingTheme.value.logo ? brandingTheme.value.logo : stLogo));

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.crossOrigin = "anonymous";
    script.src = src;

    script.onload = () => resolve();
    script.onerror = (err) => reject(err);

    document.body.append(script);
  });
}

function markSdkLoaded() {
  isSDKLoading.value = false;

  if (!props.hasWasmCrashed) {
    window.localStorage.setItem("__scanner_wasm_init__", "loaded");
  }
}

async function loadSDK() {
  const wasmSDKFile = useWorkers ? "./wasm_st_sdk.js" : "./wasm_st_sdk_no_thread.js";

  const primaryUrl = new URL(wasmSDKFile.toString(), document.baseURI).href;

  const videoBasePath = import.meta.env.VITE_SDK_BASE_PATH;
  const fallbackUrl = new URL(`${videoBasePath}/${wasmSDKFile}`, document.baseURI).href;

  try {
    await loadScript(primaryUrl);
    markSdkLoaded();
  } catch (primaryErr) {
    console.warn("SDK primary loading error:", primaryErr);
    try {
      console.warn(`Falling back to SDK from: ${fallbackUrl}`);
      await loadScript(fallbackUrl);
      markSdkLoaded();
    } catch (fallbackErr) {
      console.error("SDK fallback loading error:", fallbackErr);
      Sentry.captureException(new Error("SDK fallback loading error", { cause: fallbackErr }));
      // Redirect to photo-auth as final fallback
      redirectToPhotoAuth();
    }
  }
}

async function getCustomPrefixes() {
  const custom_prefix_url = import.meta.env.VITE_CUSTOM_PREFIX_URL;
  const res = await fetch(custom_prefix_url, {
    method: "GET"
  }).then((response) => response.json());
  // TODO: handle format versions
  return res.prefixes;
}

async function getDeviceSettings() {
  if (props.deviceInfo.model) {
    Sentry.setContext("Device Model", {
      device: props.deviceInfo.model
    });
    return await getSettings(props.deviceInfo.model, props.deviceInfo.isIos());
  }
  return Promise.reject(new Error("No device model"));
}

function handleDeniedCameraPermission() {
  props.toggleCameraAccessModal();
}

const updateProgress = () => {
  let interval = window.setInterval(() => {
    if (percentage.value < 80) {
      percentage.value += 10;
    } else if (percentage.value >= 80 && percentage.value < 95) {
      percentage.value += 5;
    } else {
      percentage.value += 1;
    }
    if (percentage.value >= 100) {
      percentage.value = 99;
      clearInterval(interval);
    }
  }, 500);
};

async function setupLocation() {
  return getLocation()
    .then(({ latitude, longitude, accuracy }) => {
      hasLocationPermission.value = true;
      isLocationPermissionDenied.value = false;
      coordinates.value = {
        latitude,
        longitude,
        accuracy
      };
    })
    .catch(() => {
      hasLocationPermission.value = false;
      isLocationPermissionDenied.value = true;
    });
}

async function setupCameraPermissionTriggering() {
  console.log("Setting up camera permission");
  cameraPermissionRequested.value = true;

  const constraints = {
    audio: false,
    video: { facingMode: { exact: "environment" } }
  };

  return navigator.mediaDevices
    .getUserMedia(constraints)
    .then((stream) => {
      stream.getVideoTracks().forEach(function (track) {
        track.stop();
      });
      hasCameraPermission.value = true;
    })
    .catch((err) => {
      hasCameraPermission.value = false;
      // NotAllowedError vs other errors
      switch (err.name) {
        case "NotAllowedError":
          handleDeniedCameraPermission();
          break;
        default:
          redirectToPhotoAuth();
      }

      return Promise.reject(new Error("Camera error", { cause: err }));
    });
}

async function setupSDKLoading() {
  const [rules] = await Promise.all([loadThirdPartyContentRules(), loadSDK(), window.wasmReady]);

  registerRules(rules);
}

async function setupDeviceSettings() {
  await getDeviceSettings()
    .then((deviceSettings) => {
      settings.value = deviceSettings;
      isSettingsLoading.value = false;
      console.log("settings.value:", settings.value);
      if (!settings.value.device_supported) {
        // TODO: should we display a specific screen?
        //       BTW we should never arrive here if we
        //       went through the lobby as we would have been
        //       redirected to photo-auth there.
        redirectToPhotoAuth();
      }
    })
    .catch((reason) => {
      console.error("Settings error:", reason);
      isSettingsLoading.value = false;
      Sentry.captureException(new Error(`Settings error: ${reason}`));
      redirectToPhotoAuth();
    });
}

async function setupCustomPrefix() {
  await getCustomPrefixes()
    .then((prefixes) => {
      prefix.value = prefixes;
      isPrefixLoading.value = false;
    })
    .catch((err) => {
      console.error("Prefix error:", err);
      Sentry.captureException(new Error(`Prefix error: ${err}`));
      redirectToPhotoAuth();
    });
}

function initialize() {
  if (isRedirectingToPhotoAuth()) {
    // A setup step failed and navigation to photo auth is in flight;
    // starting the scanner now would race the redirect.
    return;
  }
  isInitializing.value = true;
  console.log("Device:", JSON.stringify(props.deviceInfo));
  if (hasCameraPermission.value && (!isWechat || !props.isCameraOpening)) {
    props.onInitialized({
      installId: installId.value,
      prefix: prefix.value,
      settings: settings.value,
      coordinates: coordinates.value
    });
  }
}

function setup() {
  initInstallId();

  // For WeChat, we'll handle camera permissions separately
  const setupPromises = [setupLocation(), setupSDKLoading(), setupDeviceSettings(), setupCustomPrefix()];

  // Only request camera permission immediately for non-WeChat browsers
  if (!isWechat) {
    setupPromises.push(setupCameraPermissionTriggering());
  }

  Promise.all(setupPromises).then(() => {
    if (!isWechat) {
      updateProgress();
      initialize();
    }
  });
}

function initializeWechatCamera() {
  emit("wechatInitializeClick");
  const startInitialization = () => {
    if (hasCameraPermission.value && !isSDKLoading.value && !isPrefixLoading.value && !isSettingsLoading.value && !isInitializing.value) {
      initialize();
      updateProgress();
    }
  };

  if (!cameraPermissionRequested.value) {
    setupCameraPermissionTriggering().then(() => {
      startInitialization();
    });
  } else {
    startInitialization();
  }
}

// If the phone is initially in landscape mode, it would not begin setup
let orientationListener;
onMounted(() => {
  if (!isLandscapeOrientation()) {
    setup();
  } else {
    orientationListener = onBrowserOrientationChange((isLandscape) => {
      if (!isLandscape) {
        setup();
        // remove Orientation Listener
        orientationListener();
      }
    });
  }
});

const disableButton = computed(() => {
  return (
    (!hasCameraPermission.value && cameraPermissionRequested.value) ||
    props.isCameraOpening ||
    isSDKLoading.value ||
    isPrefixLoading.value ||
    isSettingsLoading.value ||
    isInitializing.value
  );
});

onUnmounted(() => {
  if (orientationListener) {
    // Remove orientation listener
    orientationListener();
  }
});
</script>

<template>
<div
  class="text_all fixed top-0 right-0 left-0 flex h-full flex-col items-center justify-center bg-[var(--background)] text-center text-[var(--text)]"
  :class="[isInitializing ? 'gap-44' : 'gap-56']"
>
  <div class="flex flex-col items-center gap-3.5">
    <img :src="mainLogo" class="h-full w-48" alt="" crossorigin />
    <p v-if="!isWechat || isInitializing" class="text-lg text-[var(--text)]">
      {{ $t("launch") }} {{ percentage }}%
    </p>
  </div>
  <button
    v-if="isWechat"
    class="mt-6 inline-flex h-10 min-w-[142px] items-center justify-center rounded px-4 py-1.5 text-center text-base leading-[18.75px] font-medium text-[var(--text-button)]"
    :class="[disableButton ? 'bg-[#B8B8B8]' : 'bg-[var(--primary)]']"
    :disabled="disableButton"
    @click.prevent
    @click="initializeWechatCamera()"
  >
    {{ isCameraOpening ? $t("camera.opening") : $t("scan.start_scan") }}
  </button>
</div>
</template>
